/**
 * useReceiveStockUndo — BLU-3xx Phase 47.01.2-B-04
 *
 * Wires post-receive sonner toast with a 30-second Undo action that calls
 * `inventory.reverseReceipt` (B-02). On click, the toast is dismissed,
 * the reversal mutation fires, and the inventory cache is invalidated.
 *
 * Per FE-5 / FD-F6 decision in plan B-04: show for ALL receives (own-org +
 * cross-org). Single behaviour > conditional behaviour — operators learn
 * "every receive = 30s undo," no branching mental model. own-org misroutes
 * (wrong SKU, wrong qty) happen too.
 *
 * Position: bottom-right (sonner default) — avoids collision with floating
 * action bars on the matrix view.
 */
import { useCallback } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import { trpc } from '@/lib/trpc';

interface ShowReceiveUndoArgs {
  /** transaction_id surfaced from atomic_increment_stock_v2. */
  transactionId: string;
  /** Human label for the toast — e.g. "100 × BACKPACK-RED for Mini Zali". */
  displayLabel: string;
}

export function useReceiveStockUndo() {
  const reverseReceipt = trpc.inventory.reverseReceipt.useMutation();
  const utils = trpc.useUtils();
  const { t } = useTranslation('inventory');

  return useCallback(
    ({ transactionId, displayLabel }: ShowReceiveUndoArgs) => {
      // Capture the toast id so we can dismiss it explicitly when the action
      // fires — sonner replaces with success/error toasts so the operator
      // sees a clean confirmation rather than two stacked toasts.
      const toastId = toast(t('receiveUndo.toast', { label: displayLabel }), {
        action: {
          label: t('receiveUndo.action'),
          // BLU-3xx Phase 47.01.2-B-fix HIGH-2: keep onClick synchronous.
          // Sonner's action onClick has signature `() => void`; an async
          // body returns an unhandled Promise. `toast.promise` already
          // owns the loading/success/error lifecycle, so we just fire it
          // and return — no outer await.
          onClick: () => {
            // Dismiss the original toast immediately so the user gets fast
            // feedback that their click was registered.
            toast.dismiss(toastId);
            // Show a "reversing…" loading toast that gets swapped on
            // success/error. The promise tracks both the reversal RPC and
            // the cache invalidation that follows it.
            toast.promise(
              reverseReceipt.mutateAsync({ transactionId }).then(async () => {
                await utils.inventory.invalidate();
              }),
              {
                loading: t('receiveUndo.reversing'),
                success: t('receiveUndo.reversed'),
                error: t('receiveUndo.failed'),
              },
            );
          },
        },
        // BLU-3xx Phase 47.01.2-B-fix3.3: dismiss action so the operator
        // can close the toast immediately when they're confident in the
        // receive (most common path). Sonner renders cancel + action
        // side-by-side, with cancel styled subtly per its design system.
        cancel: {
          label: t('receiveUndo.dismiss'),
          onClick: () => toast.dismiss(toastId),
        },
        duration: 30_000,
      });
    },
    [reverseReceipt, utils, t],
  );
}
